import { Component, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { AuthService } from '../../auth';
import { ShowsItemRef } from '../shows.model';
import { ShowsService } from '../shows.service';

/**
 * The signed-in reader's own like of a show, episode, clip or tool. Likes are private: there is
 * no count, and a reader who is not signed in sees nothing. Admins read the counts in Nova.
 */
@Component({
  selector: 'multisite-shows-like-button',
  imports: [TranslocoModule],
  providers: [provideTranslocoScope('social')],
  template: `
    @if (user()) {
      <button
        type="button"
        class="btn btn-sm"
        *transloco="let t"
        [class.btn-primary]="liked()"
        [class.btn-outline-primary]="!liked()"
        [attr.aria-pressed]="liked()"
        [disabled]="busy()"
        (click)="toggle()">
        <i class="fa-heart mr-1" [class.fa-solid]="liked()" [class.fa-regular]="!liked()"></i>
        {{ liked() ? t('social.unlike') : t('social.like') }}
      </button>
    }
  `,
})
export class ShowsLikeButtonComponent {
  private shows = inject(ShowsService);
  private authService = inject(AuthService);

  item = input.required<ShowsItemRef>();

  readonly user = toSignal(this.authService.user);
  readonly liked = signal(false);
  readonly busy = signal(false);

  constructor() {
    effect(() => {
      const ref = this.item();

      if (!this.user()) {
        return;
      }

      this.shows.getLike(ref).subscribe({ next: (liked) => this.liked.set(liked), error: () => this.liked.set(false) });
    });
  }

  toggle(): void {
    if (this.busy()) {
      return;
    }

    this.busy.set(true);
    this.shows.setLike(this.item(), !this.liked()).subscribe({
      next: (liked) => {
        this.liked.set(liked);
        this.busy.set(false);
      },
      error: () => this.busy.set(false),
    });
  }
}
